import axios from "axios";

// Keep local development usable even when an .env file has not been created.
// Production should set REACT_APP_BACKEND_URL to the deployed API origin.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

// Session lives in the API's HttpOnly cookie. These keys are purged so an old
// bundle cannot leave a readable token behind.
const TOKEN_KEYS = ["cas_access_token", "cas_refresh_token"];
/** In-memory fallback when Chrome blocks storage (SecurityError on Window.localStorage). */
const memoryStore = new Map();

let refreshPromise = null;

function purgeJsTokens() {
  for (const key of TOKEN_KEYS) {
    memoryStore.delete(key);
    withStorage("local", (s) => { s.removeItem(key); });
    withStorage("session", (s) => { s.removeItem(key); });
    clearCookie(key);
  }
}

function withStorage(kind, fn) {
  try {
    // Must touch storage inside try — passing localStorage as an arg throws before catch.
    const storage = kind === "session" ? window.sessionStorage : window.localStorage;
    return fn(storage);
  } catch {
    return undefined;
  }
}

function clearCookie(name) {
  try {
    const secure = typeof window !== "undefined" && window.location?.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
  } catch {
    /* ignore */
  }
}

export function getStoredToken() {
  return null;
}

export function getStoredRefreshToken() {
  return null;
}

/** @param {string} _token @param {{ remember?: boolean }} [_opts] */
export function setStoredToken(_token, _opts = {}) {
  purgeJsTokens();
}

/** @param {string} _token @param {{ remember?: boolean }} [_opts] */
export function setStoredRefreshToken(_token, _opts = {}) {
  purgeJsTokens();
}

/** Cookies are set by the API. Never copy them into JS-readable storage. */
export function setAuthTokens() {
  purgeJsTokens();
}

export function clearStoredToken() {
  purgeJsTokens();
}

/** The HttpOnly API cookie survives the Google redirect. Nothing to copy. */
export function persistAuthForExternalRedirect() {
  purgeJsTokens();
}

purgeJsTokens();

/** Safe localStorage helpers for UI prefs (Remember me, etc.). */
export function safeLocalGet(key, fallback = null) {
  const v = withStorage("local", (s) => s.getItem(key));
  if (v != null) return v;
  return memoryStore.has(key) ? memoryStore.get(key) : fallback;
}

export function safeLocalSet(key, value) {
  memoryStore.set(key, value);
  withStorage("local", (s) => { s.setItem(key, value); });
}

export function safeLocalRemove(key) {
  memoryStore.delete(key);
  withStorage("local", (s) => { s.removeItem(key); });
}

/** Rotate the HttpOnly session cookie. Dedupes concurrent callers. */
export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const { data } = await axios.post(
      `${API_BASE}/api/auth/refresh`,
      {},
      { withCredentials: true, headers: { "Content-Type": "application/json" } },
    );
    if (!data?.ok) throw new Error("Refresh failed");
    purgeJsTokens();
    return true;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  config.withCredentials = true;
  return config;
});

// Handle 401s — try refresh once, then retry original request
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config;
    if (
      err?.response?.status === 401 &&
      config &&
      !config._retry &&
      !config.url?.includes("/auth/login") &&
      !config.url?.includes("/auth/refresh")
    ) {
      config._retry = true;
      try {
        await refreshAccessToken();
        if (config.headers) delete config.headers.Authorization;
        return api.request(config);
      } catch (e) {
        clearStoredToken();
      }
    }
    return Promise.reject(err);
  },
);

export const API_URL = API_BASE;
export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
